import React, { ReactNode, useCallback, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { Checkbox, CheckboxSize } from './Checkbox';
import { AlignType } from '../radio';
import { useControlled } from '../../hooks/useControlled';
import { getKeyFromItem } from '../../utils/shared';
import { TextButton } from '../button';
import { Stack } from '../stack';
import { AriaFocusProps } from '../../types/common';
import { buildCSSWithLength } from '../../styles';
import { getInputHeightBySize } from '../input/styles';
import { useProviderConfig } from '../provider';

export interface CheckboxItem<CheckboxValue> {
  label: ReactNode;
  value: CheckboxValue;
  disabled?: boolean;
  link?: string;
  onClickLink?: (e: CheckboxValue) => void;
}

export interface CheckboxGroupProps<CheckboxValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'value' | 'onChange'>,
    AriaFocusProps {
  className?: string;
  defaultValue?: CheckboxValue[];
  value?: CheckboxValue[];
  /** @default false */
  disabled?: boolean;
  name?: string;
  /** @default 24 */
  spacing?: number;
  items: CheckboxItem<CheckboxValue>[];
  /** @default 'horizontal' */
  align?: AlignType;
  /** @default 'medium' */
  size?: CheckboxSize;
  /**
   * 각 체크박스 아이템에서 고유 key를 추출하는 함수
   */
  keyExtractor?: (item: CheckboxItem<CheckboxValue>, index: number) => string | number;
  onChange?: (value: CheckboxValue[]) => void;
  /** @default false */
  hasSelectAll?: boolean;
}

export interface CheckboxGroupInstance<CheckboxValue> {
  value?: CheckboxValue[];
  focus: () => void;
}

type CheckboxGroupComponent = <CheckboxValue extends string | number | boolean>(
  props: CheckboxGroupProps<CheckboxValue> & { ref?: React.Ref<CheckboxGroupInstance<CheckboxValue>> },
) => JSX.Element;

export const CheckboxGroup = React.forwardRef(
  <CheckboxValue extends string | number | boolean>(
    {
      className,
      name,
      items,
      defaultValue = [],
      value,
      spacing = 24,
      disabled = false,
      align = 'horizontal',
      size = 'medium',
      unstableFocusVisible,
      keyExtractor = getKeyFromItem,
      onChange,
      hasSelectAll = false,
      ...otherProps
    }: CheckboxGroupProps<CheckboxValue>,
    ref: React.Ref<CheckboxGroupInstance<CheckboxValue>>,
  ) => {
    const { locale } = useProviderConfig();
    const [selected_values, setSelectedValues] = useControlled({ defaultValue, value });
    const first_ref = useRef<HTMLInputElement | null>(null);

    const handleChange = useCallback(
      (item_value: CheckboxValue) => {
        const is_checked = !selected_values.includes(item_value);
        const new_values = is_checked
          ? [...selected_values, item_value]
          : selected_values.filter((value) => String(value) !== String(item_value));

        setSelectedValues(new_values);
        onChange?.(new_values);
      },
      [setSelectedValues, selected_values],
    );

    useImperativeHandle(
      ref,
      () => ({
        value: selected_values,
        focus: () => first_ref.current?.focus?.(),
      }),
      [selected_values],
    );

    return (
      <Container
        className={classNames('pds-checkbox-group', className)}
        align={align}
        spacing={spacing}
        size={size}
        role='group'
        {...otherProps}
      >
        {hasSelectAll && (
          <Checkbox
            size={size}
            checked={selected_values.length === items.length}
            onChange={() => {
              const new_values = selected_values.length === items.length ? [] : items.map((item) => item.value);
              setSelectedValues(new_values);
              onChange?.(new_values);
            }}
            disabled={disabled}
            unstableFocusVisible={unstableFocusVisible}
            ref={first_ref}
          >
            {locale?.CheckboxGroup.all}
          </Checkbox>
        )}
        {items.map((item, index) => {
          const getCommonCheckboxProps = () => ({
            size: size,
            disabled: disabled || item.disabled,
            checked: selected_values?.includes(item.value),
            onChange: () => handleChange(item.value),
            name,
            unstableFocusVisible,
            ref: index === 0 && !hasSelectAll ? first_ref : undefined,
          });

          return item.link ? (
            <Wrapper direction='row' align='flex-start' key={keyExtractor(item, index)} justify='space-between'>
              <Checkbox {...getCommonCheckboxProps()}>{item.label}</Checkbox>
              {item.link && (
                <TextButton
                  disabled={disabled || item.disabled}
                  variant='link'
                  onClick={() => item.onClickLink?.(item.value)}
                >
                  {item.link}
                </TextButton>
              )}
            </Wrapper>
          ) : (
            <Checkbox key={keyExtractor(item, index)} {...getCommonCheckboxProps()}>
              {item.label}
            </Checkbox>
          );
        })}
      </Container>
    );
  },
  // React 18 forwardRef는 render 함수의 CheckboxValue 제네릭을 보존하지 않으므로 공개 시그니처를 복구합니다.
) as CheckboxGroupComponent;

const Container = styled.div<{ align?: AlignType; spacing: Number; size: CheckboxSize }>`
  display: flex;
  flex-wrap: wrap;
  row-gap: 16px;
  ${({ align, spacing }) => (align === 'horizontal' ? `column-gap: ${spacing}px` : `row-gap: ${spacing}px`)};
  ${({ align, size }) =>
    align === 'horizontal'
      ? `
        ${buildCSSWithLength('min-height', getInputHeightBySize(size))}
        align-items: center;
        flex-direction: row;
        `
      : `
        min-height: auto;
        align-items: flex-start;
        flex-direction: column;
  `};
`;

const Wrapper = styled(Stack)`
  width: 100%;
  column-gap: 8px;
`;
